import { useState } from 'react';
import { Outlet, Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import CartBar from '../cart/CartBar';

interface Props {
  requireAuth?: boolean;
  requireRole?: 'host' | 'admin';
}

export default function MainLayout({ requireAuth, requireRole }: Props) {
  const { user, signOut, hasRole } = useAuthStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (requireAuth && !user) return <Navigate to="/signin" />;
  if (requireRole && !hasRole(requireRole)) return <Navigate to="/" />;

  const closeMenu = () => setMenuOpen(false);

  const navLinks = (
    <>
      <Link to="/" onClick={closeMenu} className="text-slate-500 hover:text-slate-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -ml-1">차량 찾기</Link>
      {user && (
        <>
          <Link to="/reservations" onClick={closeMenu} className="text-slate-500 hover:text-slate-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -ml-1">내 예약</Link>
          <Link to="/profile" onClick={closeMenu} className="text-slate-400 hover:text-slate-700 transition-colors duration-150 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -ml-1">내 정보</Link>
        </>
      )}
      {hasRole('host') && (
        <>
          <Link to="/host" onClick={closeMenu} className="text-slate-500 hover:text-slate-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -ml-1">호스트</Link>
          <Link to="/host/vehicles/new" onClick={closeMenu} className="text-brand-600 hover:text-brand-700 transition-colors duration-150 font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -ml-1">차량 등록</Link>
        </>
      )}
      {hasRole('admin') && (
        <Link to="/admin" onClick={closeMenu} className="text-accent-600 hover:text-accent-700 transition-colors duration-150 font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -ml-1">관리자</Link>
      )}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-8">
            {/* Mobile hamburger */}
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-slate-700 p-1 rounded-lg transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            <Link to="/" className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">
              PURPLEMINE
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
              {navLinks}
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <>
                <span className="hidden sm:inline text-sm text-slate-400">{user.name}님</span>
                <button onClick={signOut} className="text-xs sm:text-sm text-slate-400 hover:text-slate-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -ml-1">
                  로그아웃
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/signin')} className="text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-900 px-2 sm:px-4 py-2 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
                  로그인
                </button>
                <button onClick={() => navigate('/signup')} className="text-xs sm:text-sm font-medium bg-slate-900 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2">
                  가입하기
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile nav overlay */}
        {menuOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/40" onClick={closeMenu} />
            <div className="relative w-64 bg-white h-full flex flex-col shadow-xl animate-slide-in">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <span className="font-bold text-slate-900">PURPLEMINE</span>
                <button onClick={closeMenu} className="text-gray-400 hover:text-gray-700 text-2xl leading-none transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-lg">&times;</button>
              </div>
              <nav className="flex flex-col gap-1 p-4 text-sm font-medium">
                {navLinks}
              </nav>
              {user && (
                <div className="mt-auto p-4 border-t border-gray-100">
                  <p className="text-sm text-slate-900 font-medium">{user.name}님</p>
                  <button onClick={() => { signOut(); closeMenu(); }} className="text-xs text-gray-400 hover:text-red-500 mt-1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">로그아웃</button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-400">
          © 2024 PURPLEMINE. 촬영용 차량 대여 플랫폼
        </div>
      </footer>

      {/* Floating Cart Bar */}
      <CartBar />
    </div>
  );
}
